import { bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';

import type { CustomerRecord, PetRecord } from '../appointments/types';
import type { CustomerProfile, CustomerSummary } from './types';

export interface UpdateCustomerNotesPayload {
  customerId: string;
  internalNotes: string;
}

export interface CreateCustomerPayload {
  fullName: string;
  phone?: string | null;
  email?: string | null;
  isVip?: boolean;
  tags?: string[];
  internalNotes?: string | null;
  preferredContactChannel?: CustomerRecord['preferredContactChannel'] | null;
}

export interface CreatePetPayload {
  customerId: string;
  name: string;
  species: PetRecord['species'];
  breed?: string | null;
  birthDate?: string | null;
  sex?: PetRecord['sex'] | null;
  neutered?: boolean | null;
  weightKg?: number | null;
  allergies?: string[];
  chronicConditions?: string[];
  behaviorNotes?: string | null;
  microchipId?: string | null;
  photoUrl?: string | null;
}

interface ListEnvelope<T> {
  readonly items: T[];
}

interface SingleEnvelope<T> {
  readonly item: T | null;
}

export async function listCustomerSummaries(): Promise<CustomerSummary[]> {
  const response = await bizGet<ListEnvelope<CustomerSummary>>('/biz/customers');
  return response.items;
}

export async function getCustomerProfile(
  customerId: string,
): Promise<CustomerProfile | null> {
  return bizGet<CustomerProfile>(`/biz/customers/${customerId}`);
}

export async function updateCustomerNotes(
  payload: UpdateCustomerNotesPayload,
): Promise<CustomerRecord> {
  return bizPatch<CustomerRecord, { internalNotes: string }>(
    `/biz/customers/${payload.customerId}/notes`,
    { internalNotes: payload.internalNotes },
  );
}

export async function createCustomer(
  payload: CreateCustomerPayload,
): Promise<CustomerRecord> {
  return bizPost<CustomerRecord, CreateCustomerPayload>('/biz/customers', payload);
}

export async function findDuplicateCustomerByPhone(
  phone: string,
): Promise<CustomerRecord | null> {
  const response = await bizGet<SingleEnvelope<CustomerRecord>>('/biz/customers/duplicates', {
    params: { phone },
  });
  return response.item;
}

export async function listPets(customerId?: string): Promise<PetRecord[]> {
  const response = await bizGet<ListEnvelope<PetRecord>>('/biz/pets', {
    params: customerId ? { customerId } : undefined,
  });
  return response.items;
}

export async function createPet(payload: CreatePetPayload): Promise<PetRecord> {
  return bizPost<PetRecord, CreatePetPayload>('/biz/pets', payload);
}
